import { z } from "zod/v4";
import {
	type BadgeStatus,
	adminBadgeListResponseSchema,
	invoiceResponseSchema,
	markOverdueInvoicesResponseSchema,
	membershipFeeResponseSchema,
	paymentDashboardResponseSchema,
	paymentResponseSchema,
} from "../schemas/finance";
import { fetchApi, fetchVoid, paginatedSchema } from "./internal";

/** Server clamps `limit` at 100 — one full page is the whole review queue in practice. */
export const BADGE_QUEUE_PAGE_SIZE = 100;
export const DEFAULT_BADGE_QUEUE_STATUS: BadgeStatus = "pending_review";

export interface BadgeListParams {
	status?: BadgeStatus;
	limit?: number;
	offset?: number;
}

/** The queue's default request: everything pending review, at the max page size. */
export function badgeVerificationQueueParams(): BadgeListParams {
	return { status: DEFAULT_BADGE_QUEUE_STATUS, limit: BADGE_QUEUE_PAGE_SIZE, offset: 0 };
}

export const finance = {
	invoices: {
		list: (clubId: string, status?: string) => {
			const params = new URLSearchParams();
			if (status) {
				params.set("status", status);
			}
			const query = params.toString();
			return fetchApi(
				`/finance/admin/clubs/${clubId}/invoices${query ? `?${query}` : ""}`,
				paginatedSchema(invoiceResponseSchema),
			);
		},
		markOverdue: (clubId: string) =>
			fetchApi(
				`/finance/admin/clubs/${clubId}/invoices/mark-overdue`,
				markOverdueInvoicesResponseSchema,
				{ method: "POST" },
			),
	},
	payments: {
		list: (clubId: string) =>
			fetchApi(`/finance/admin/clubs/${clubId}/payments`, paginatedSchema(paymentResponseSchema)),
	},
	dashboard: (clubId: string) =>
		fetchApi(`/finance/admin/clubs/${clubId}/payments/dashboard`, paymentDashboardResponseSchema),
	membershipFees: {
		list: (clubId: string) =>
			fetchApi(`/finance/admin/clubs/${clubId}/fees`, z.array(membershipFeeResponseSchema)),
	},
	badges: {
		list: (params?: BadgeListParams) => {
			const search = new URLSearchParams();
			if (params?.status) {
				search.set("status", params.status);
			}
			if (params?.limit !== undefined) {
				search.set("limit", String(params.limit));
			}
			if (params?.offset !== undefined) {
				search.set("offset", String(params.offset));
			}
			const query = search.toString();
			return fetchApi(
				`/finance/admin/badges${query ? `?${query}` : ""}`,
				adminBadgeListResponseSchema,
			);
		},
		approve: (badgeId: string) =>
			fetchVoid(`/finance/admin/badges/${badgeId}`, {
				method: "PATCH",
				body: JSON.stringify({ status: "approved" }),
			}),
		reject: (badgeId: string, reason?: string) =>
			fetchVoid(`/finance/admin/badges/${badgeId}`, {
				method: "PATCH",
				body: JSON.stringify({ status: "rejected", rejection_reason: reason }),
			}),
	},
};
